/**
 * The data table, its skeleton rows and the pager. Screens write the <thead>
 * and <tbody> themselves and style cells with `tableClass`.
 */
import type { CSSProperties, ReactNode } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Skeleton } from './data'
import { Button, IconButton, cx } from './primitives'
import styles from './Table.module.css'

export interface TableProps {
  children: ReactNode
  /** Header row sticks under the top bar while the page scrolls (default true). */
  stickyHeader?: boolean
  /** Rows fade up in a 25 ms stagger when they appear (default true). */
  stagger?: boolean
  /** Faint alternate row fill. */
  zebra?: boolean
  /** 36 px rows instead of 44. */
  dense?: boolean
  /** Scroll inside the table past this height (the header then sticks to the table's own top). */
  maxHeight?: number
  /** Names the table for assistive tech. */
  ariaLabel?: string
  /** Marks the table as loading (aria-busy). */
  busy?: boolean
  className?: string
}

/**
 * The table: caption-style sticky header, 44 px rows with a hover fill,
 * right-aligned mono figures via `tableClass.right` + `num`, stagger on new
 * rows. It scrolls sideways only when the columns do not fit; otherwise the
 * header sticks to the page (under `--topbar-h`, default 56 px).
 */
export function Table({ children, stickyHeader = true, stagger = true, zebra = false, dense = false, maxHeight, ariaLabel, busy, className }: TableProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [overflows, setOverflows] = useState(false)

  const [fade, setFade] = useState<'start' | 'end' | 'both' | undefined>(undefined)

  useLayoutEffect(() => {
    const wrap = wrapRef.current
    const table = tableRef.current
    if (!wrap || !table || typeof ResizeObserver === 'undefined') return
    // When the columns do not fit, the hidden side fades out so the sideways scroll is visible.
    const edges = (): void => {
      const more = table.scrollWidth > wrap.clientWidth + 1
      const atStart = wrap.scrollLeft <= 1
      const atEnd = wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 1
      setFade(!more ? undefined : atStart && !atEnd ? 'end' : atEnd && !atStart ? 'start' : !atStart && !atEnd ? 'both' : undefined)
    }
    const measure = (): void => {
      setOverflows(table.scrollWidth > wrap.clientWidth + 1)
      edges()
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(wrap)
    observer.observe(table)
    wrap.addEventListener('scroll', edges, { passive: true })
    return () => {
      observer.disconnect()
      wrap.removeEventListener('scroll', edges)
    }
  }, [])

  const scrolls = maxHeight !== undefined
  return (
    <div
      ref={wrapRef}
      className={cx(styles.wrap, overflows || scrolls ? styles.scrollX : '', scrolls ? styles.scrollY : '', className)}
      style={scrolls ? ({ maxHeight } as CSSProperties) : undefined}
      data-overflow={overflows ? '' : undefined}
      data-fade={fade}
    >
      <table
        ref={tableRef}
        className={cx(styles.table, stickyHeader && (!overflows || scrolls) ? styles.sticky : '', scrolls ? styles.stickyLocal : '', stagger ? styles.stagger : '', zebra ? styles.zebra : '', dense ? styles.dense : '')}
        aria-label={ariaLabel}
        aria-busy={busy || undefined}
      >
        {children}
      </table>
    </div>
  )
}

/** Cell and row classes: right (numbers), wrap (long text), muted, actions (buttons, right), reveal (actions shown on row hover/focus), clickable/selected rows, num (mono right), nowrap, shrink (fits content). */
export const tableClass = {
  right: styles.right,
  wrap: styles.wrapCell,
  muted: styles.muted,
  actions: styles.actions,
  clickable: styles.clickable,
  selected: styles.selected,
  reveal: styles.reveal,
  num: styles.num,
  nowrap: styles.nowrap,
  shrink: styles.shrink,
  /** A second line in a cell (date under an email, network under an asset). */
  sub: styles.sub,
}

const SKELETON_WIDTHS = ['72%', '48%', '60%', '38%', '82%', '54%', '66%', '44%']

/** Placeholder <tr>s with shimmering bars, for inside an existing <tbody>. */
export function SkeletonRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }): JSX.Element {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} className={styles.skeletonRow} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c} className={c === columns - 1 && columns > 2 ? styles.right : undefined}>
              <Skeleton width={SKELETON_WIDTHS[(r * 3 + c * 5) % SKELETON_WIDTHS.length]} height={10} className={c === columns - 1 && columns > 2 ? styles.skeletonRight : undefined} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

/** A whole loading table: the real header labels when given, shimmer rows below. */
export function TableSkeleton({ rows = 6, columns, headers }: { rows?: number; columns?: number; headers?: readonly ReactNode[] }): JSX.Element {
  const count = columns ?? headers?.length ?? 5
  return (
    <Table busy ariaLabel="Загрузка" stagger={false}>
      <thead>
        <tr>
          {Array.from({ length: count }, (_, c) => (
            <th key={c} className={c === count - 1 && count > 2 ? styles.right : undefined}>
              {headers?.[c] ?? <Skeleton width={56} height={8} className={c === count - 1 && count > 2 ? styles.skeletonRight : undefined} />}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <SkeletonRows rows={rows} columns={count} />
      </tbody>
    </Table>
  )
}

/** Page window around the current page: 1 … 4 5 [6] 7 8 … 20. */
function pageWindow(page: number, pages: number): Array<number | 'gap'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)
  const out: Array<number | 'gap'> = [1]
  const from = Math.max(2, page - 1)
  const to = Math.min(pages - 1, page + 1)
  if (from > 2) out.push('gap')
  for (let p = from; p <= to; p++) out.push(p)
  if (to < pages - 1) out.push('gap')
  out.push(pages)
  return out
}

/** Pagination under a table: «1–50 из 231», page numbers, previous/next; hidden for a single page. */
export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (page: number) => void }): JSX.Element | null {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (pages <= 1) return null
  const first = (page - 1) * pageSize + 1
  const last = Math.min(total, page * pageSize)
  return (
    <nav className={styles.pager} aria-label="Страницы">
      <span className={styles.pagerRange}>
        <span className={styles.num}>
          {first}–{last}
        </span>{' '}
        из <span className={styles.num}>{total}</span>
      </span>
      <div className={styles.pagerPages}>
        <IconButton icon={<ChevronLeft />} label="Предыдущая страница" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} tooltip={false} />
        {pageWindow(page, pages).map((item, i) =>
          item === 'gap' ? (
            <span key={`gap-${i}`} className={styles.pagerGap} aria-hidden="true">
              …
            </span>
          ) : (
            <Button
              key={item}
              size="sm"
              variant="ghost"
              className={cx(styles.pagerPage, item === page ? styles.pagerCurrent : '')}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPage(item)}
            >
              {item}
            </Button>
          ),
        )}
        <IconButton icon={<ChevronRight />} label="Следующая страница" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} tooltip={false} />
      </div>
    </nav>
  )
}
